import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-number-input/react-hook-form-input';
import 'react-phone-number-input/style.css';
import { isPossiblePhoneNumber } from 'react-phone-number-input';

import { apiUpdateOrganization } from 'Redux/actions/organization';
import Loader from 'Components/Loader';
import ReactCountrySelect from 'Components/ReactCountrySelect';
import { FormErrorText } from '../../Components/CommonStyles';
import { OutlinedButton } from '../../Components/IconButton/styles';
import {
  FormContent,
  FormDescription,
  FormFooter,
  FormHeader,
  FormHeaderButtons,
  FormHeaderTitleColumn,
  FormLabel,
  FormRow,
  FormTitle,
  SaveButton,
} from '../AccountPage/styles';
import { FieldInput } from './styles';

const REQUIRED = 'This field is required.';
const maxLength = value => ({ value, message: `Max ${value} characters.` });
const notBlank = value => value.trim() !== '' || REQUIRED;

// Mirrors the backend rules for instant feedback; the API remains the source of
// truth. Required fields are what a courier needs to ship hardware.
const TEXT_FIELDS = [
  {
    name: 'name',
    label: 'Organization name*',
    placeholder: 'Organization name',
    rules: { validate: notBlank, maxLength: maxLength(150) },
  },
  {
    name: 'contact_email',
    label: 'Contact Email*',
    placeholder: 'Contact Email',
    type: 'email',
    rules: {
      validate: notBlank,
      // Loose on purpose: the backend EmailField makes the final call.
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Enter a valid email.',
      },
      maxLength: maxLength(254),
    },
  },
  {
    name: 'address_line1',
    label: 'Address Line 1*',
    placeholder: 'Address Line 1',
    rules: { validate: notBlank, maxLength: maxLength(255) },
  },
  {
    name: 'address_line2',
    label: 'Address Line 2',
    placeholder: 'Address Line 2 (optional)',
    rules: { maxLength: maxLength(255) },
  },
  {
    name: 'city',
    label: 'City*',
    placeholder: 'City',
    rules: { validate: notBlank, maxLength: maxLength(100) },
  },
  {
    name: 'state',
    label: 'State',
    placeholder: 'State / Province (optional)',
    rules: { maxLength: maxLength(100) },
  },
  {
    name: 'postal_code',
    label: 'Postal Code*',
    placeholder: 'Postal Code',
    rules: { validate: notBlank, maxLength: maxLength(20) },
  },
];

/** One labelled field row: label column, then the input and its error. */
const FieldRow = ({ label, error, children }) => (
  <FormRow className="row">
    <FormLabel className="col-sm-3">{label}</FormLabel>
    <FieldInput className="col-sm-9">
      {children}
      {error && <FormErrorText className="form-text">{error}</FormErrorText>}
    </FieldInput>
  </FormRow>
);

const toFormValues = organization => ({
  name: organization?.name ?? '',
  contact_email: organization?.contact_email ?? '',
  contact_phone: organization?.contact_phone ?? '',
  address_line1: organization?.address_line1 ?? '',
  address_line2: organization?.address_line2 ?? '',
  city: organization?.city ?? '',
  state: organization?.state ?? '',
  postal_code: organization?.postal_code ?? '',
  country: organization?.country ?? '',
});

/** Organization details for its admins — the contact address hardware ships to. */
const OrgDetailsPage = ({ auth, selectedOrg }) => {
  const dispatch = useDispatch();
  const userInformation = useSelector(state => state.userInformation);

  const organization = userInformation.data?.organisation_data?.find(
    item => item?.organisation?.id === selectedOrg,
  )?.organisation;

  // `values` resyncs only when the content changes (deep compare), so the store
  // handing back an identical copy doesn't wipe what the user typed.
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm({ values: organization ? toFormValues(organization) : undefined });

  const onSave = handleSubmit(values => {
    dispatch(
      apiUpdateOrganization(
        auth.token,
        selectedOrg,
        values,
        auth.user_information?.id,
      ),
    );
  });

  // Loader only before first load; a background refetch must not unmount the form.
  if (!organization) return userInformation?.isLoading ? <Loader /> : null;

  const buttons = isDirty ? (
    <FormHeaderButtons>
      <OutlinedButton className="btn" onClick={() => reset(toFormValues(organization))}>
        Cancel
      </OutlinedButton>
      <SaveButton className="btn btn-primary" onClick={onSave}>
        Save
      </SaveButton>
    </FormHeaderButtons>
  ) : null;

  return (
    <>
      <FormHeader>
        <FormHeaderTitleColumn>
          <FormTitle>Organization details</FormTitle>
          <FormDescription>
            Update your organization&apos;s name and contact details here.
          </FormDescription>
        </FormHeaderTitleColumn>
        {buttons}
      </FormHeader>

      <FormContent>
        <form>
          {TEXT_FIELDS.map(field => (
            <FieldRow
              key={field.name}
              label={field.label}
              error={errors[field.name]?.message}
            >
              <Controller
                name={field.name}
                control={control}
                rules={field.rules}
                render={({ field: controlled }) => (
                  <input
                    {...controlled}
                    type={field.type ?? 'text'}
                    className="form-control"
                    placeholder={field.placeholder}
                  />
                )}
              />
            </FieldRow>
          ))}

          <FieldRow label="Country*" error={errors.country?.message}>
            <Controller
              name="country"
              control={control}
              rules={{ required: REQUIRED }}
              render={({ field: { value, onChange, onBlur } }) => (
                <ReactCountrySelect
                  value={value}
                  onChange={option => onChange(option?.value ?? '')}
                  onBlur={onBlur}
                />
              )}
            />
          </FieldRow>

          <FieldRow label="Contact Phone*" error={errors.contact_phone?.message}>
            <PhoneInput
              name="contact_phone"
              control={control}
              rules={{
                required: REQUIRED,
                validate: value =>
                  isPossiblePhoneNumber(value) || 'Invalid phone number',
              }}
              // Local numbers use the org's country; international ones start with +.
              defaultCountry={watch('country') || undefined}
              placeholder="Contact Phone"
              className="form-control"
            />
          </FieldRow>
        </form>
      </FormContent>

      <FormFooter>
        <span></span>
        {buttons}
      </FormFooter>
    </>
  );
};

export default OrgDetailsPage;
